import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';

/**
 * Sources and renders (brand/crow-extensions.html, "Sources et rendus"): an editor opens a source;
 * what is drawn from it (an MP4, a PNG, a CSV) is a render, an ordinary file the index links to
 * its source and the version it was rendered from (`derivedFrom`).
 */

type Listed = Pick<
  ArtifactListingEntry,
  'id' | 'space' | 'path' | 'conversation' | 'modifiedAt' | 'derivedFrom'
> &
  Partial<Pick<ArtifactListingEntry, 'workspace'>>;

/**
 * Whether `render` was rendered from `source`: its link names it, or its path in the same space
 * and conversation (or the same folder: another conversation of it may list the source).
 */
export function isRenderOf(render: Listed, source: Listed): boolean {
  const link = render.derivedFrom;
  if (link === undefined || link === null) return false;
  if (link.artifact === source.id) return true;
  return (
    link.path === source.path &&
    render.space === source.space &&
    (render.conversation === source.conversation ||
      (render.workspace != null && render.workspace === source.workspace))
  );
}

/** A source's renders, the latest first. */
export function rendersOf<Artifact extends Listed>(
  source: Listed,
  artifacts: readonly Artifact[],
): Artifact[] {
  return artifacts
    .filter((artifact) => artifact.id !== source.id && isRenderOf(artifact, source))
    .toSorted((left, right) => Date.parse(right.modifiedAt) - Date.parse(left.modifiedAt));
}

/** The render a preview shows of its source: the latest of its current version, else the latest. */
export function latestRender<Artifact extends Listed>(
  source: Listed,
  artifacts: readonly Artifact[],
): Artifact | null {
  const renders = rendersOf(source, artifacts);
  return renders.find((render) => render.derivedFrom?.stale !== true) ?? renders[0] ?? null;
}

/** "Render of Teaser Crowdex · v4": what a render says of its source. */
export function renderOfLabel(
  render: Pick<ArtifactListingEntry, 'derivedFrom'>,
  sourceTitle: string | null,
): string {
  const version = render.derivedFrom?.version;
  return `Render of ${sourceTitle ?? render.derivedFrom?.path ?? 'its source'}${version === undefined ? '' : ` · v${version}`}`;
}

/** A render's line under its source: its file, its version, "Old" when the source moved on. */
export function renderLineLabel(
  render: Pick<ArtifactListingEntry, 'title' | 'derivedFrom'>,
): string {
  const version = render.derivedFrom?.version;
  return [
    render.title,
    version === undefined ? null : `v${version}`,
    render.derivedFrom?.stale ? 'Old' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * One row per source, its renders under it (a home's "Yours", the Library, the summary):
 * a render whose source is listed leaves the list for its source's row; one whose source is not
 * listed stays a row of its own. `renders` default to the list itself.
 */
export function withRenders<Artifact extends Listed>(
  listed: readonly Artifact[],
  renders: readonly Artifact[] = listed,
): { artifact: Artifact; renders: Artifact[] }[] {
  const placed = new Set<string>();
  const rows = listed.map((artifact) => {
    const own = rendersOf(artifact, renders);
    for (const render of own) placed.add(render.id);
    return { artifact, renders: own };
  });
  return rows.filter((row) => !placed.has(row.artifact.id));
}
